import type {
  DownloadList,
  ErrorDetail,
  ErrorList,
  InstallationDetail,
  InstallationList,
  Overview,
  QueryPeriod,
  SystemStatus
} from "./types.js";

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

type Query = Record<
  string,
  string | number | boolean | null | undefined
>;

function queryString(query: Query = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const result = params.toString();
  return result ? `?${result}` : "";
}

function periodQuery(period: QueryPeriod): Query {
  return {
    period: period.period,
    from: period.from,
    to: period.to
  };
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers
    }
  });
  if (!response.ok) {
    let code = "request_failed";
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as {
        code?: string;
        message?: string;
      };
      code = body.code ?? code;
      message = body.message ?? message;
    } catch {
      // The stable status is enough when an upstream returned non-JSON.
    }
    throw new ApiClientError(response.status, code, message);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  authStatus: () =>
    request<{
      authentication_enabled: boolean;
      authenticated: boolean;
    }>("/api/admin/v1/auth/status"),

  login: (password: string) =>
    request<{ status: "ok" }>("/api/admin/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ password })
    }),

  logout: () =>
    request<void>("/api/admin/v1/auth/logout", { method: "POST" }),

  overview: (period: QueryPeriod, launcherVersion = "") =>
    request<Overview>(
      `/api/admin/v1/overview${queryString({
        ...periodQuery(period),
        launcher_version: launcherVersion
      })}`
    ),

  errors: (
    period: QueryPeriod,
    filters: {
      launcherVersion?: string;
      search?: string;
      fatal?: boolean;
      limit?: number;
      offset?: number;
    }
  ) =>
    request<ErrorList>(
      `/api/admin/v1/errors${queryString({
        ...periodQuery(period),
        launcher_version: filters.launcherVersion,
        search: filters.search,
        fatal: filters.fatal,
        limit: filters.limit,
        offset: filters.offset
      })}`
    ),

  errorDetail: (fingerprint: string) =>
    request<ErrorDetail>(`/api/admin/v1/errors/${fingerprint}`),

  downloads: (
    period: QueryPeriod,
    filters: {
      launcherVersion?: string;
      result?: string;
      installationType?: string;
      limit?: number;
      offset?: number;
    }
  ) =>
    request<DownloadList>(
      `/api/admin/v1/downloads${queryString({
        ...periodQuery(period),
        launcher_version: filters.launcherVersion,
        result: filters.result,
        installation_type: filters.installationType,
        limit: filters.limit,
        offset: filters.offset
      })}`
    ),

  installations: (
    period: QueryPeriod,
    filters: {
      launcherVersion?: string;
      installationType?: string;
      state?: string;
      limit?: number;
      offset?: number;
    }
  ) =>
    request<InstallationList>(
      `/api/admin/v1/installations${queryString({
        ...periodQuery(period),
        launcher_version: filters.launcherVersion,
        installation_type: filters.installationType,
        state: filters.state,
        limit: filters.limit,
        offset: filters.offset
      })}`
    ),

  installationDetail: (installationId: string) =>
    request<InstallationDetail>(
      `/api/admin/v1/installations/${installationId}`
    ),

  system: () => request<SystemStatus>("/api/admin/v1/system")
};
